import { resolveDateFilterValue } from "./dateFilterPresets";

// Splits a "YYYY-MM-DD" or "YYYY-MM-DDTHH:mm" filter string into local date and time parts.
const parseLocalParts = (value) => {
  const [date, time] = value.split("T");
  const [year, month, day] = date.split("-").map(Number);
  const [hours, minutes] = (time || "00:00").split(":").map(Number);
  return { year, month, day, hours, minutes };
};

// Parses a date or date+time string as local time and returns a UTC ISO string
const toISO = (value) => {
  if (!value) return null;
  const { year, month, day, hours, minutes } = parseLocalParts(value);
  return new Date(year, month - 1, day, hours, minutes).toISOString();
};

// The same time on the next local calendar day, as a UTC ISO string. Advances the date rather
// than adding 24 hours, so a day that gains or loses an hour at a daylight-saving change still
// ends at the next local midnight.
const addOneDay = (value) => {
  const { year, month, day, hours, minutes } = parseLocalParts(value);
  return new Date(year, month - 1, day + 1, hours, minutes).toISOString();
};

export const buildApiQuery = (params) => {
  const {
    filters,
    sort,
    sortDirection,
    page,
    pageSize,
    searchTerm,
    customerIds = [],
    includeGroups = [],
    excludeGroups = [],
    ...extraParams
  } = params;
  const query = new URLSearchParams();

  // If customerIds is provided, add them
  customerIds.forEach((id) => query.append("customerIds", id));

  // Handle groups parameters
  includeGroups.forEach((id) => query.append("includeGroups", id));
  excludeGroups.forEach((id) => query.append("excludeGroups", id));

  if (page) query.set("page", page);
  if (pageSize) query.set("pageSize", pageSize);
  if (searchTerm) query.set("search", searchTerm);
  if (sort) {
    query.set("orderBy", sort);
    if (sortDirection) {
      query.set("orderDirection", sortDirection);
    }
  }

  if (filters) {
    filters.forEach((filter) => {
      const { key, type } = filter;
      // Relative presets ("Today", "This week") resolve to dates at query time, so a saved
      // filter keeps tracking the current day rather than the day it was created.
      const value =
        type === "date" ? resolveDateFilterValue(filter.value) : filter.value;
      switch (type) {
        case "string":
          if (value) query.set(key, value);
          break;
        case "enum":
          if (Array.isArray(value)) {
            value.forEach((v) => query.append(key, v));
          }
          break;
        case "includeExcludeEnum": {
          const capKey = key.charAt(0).toUpperCase() + key.slice(1);
          const chains = Array.isArray(value) ? value : [];

          // Include chain → includeGroups (OR within)
          const includeChain = chains.find((c) => c.mode === "include");
          if (includeChain?.selected?.length > 0) {
            includeChain.selected.forEach((v) =>
              query.append(`include${capKey}`, v),
            );
          }

          // Exclude chain → excludeGroups (OR'd then NOT'd)
          const excludeValues = chains
            .filter((c) => c.mode === "exclude")
            .flatMap((c) => c.selected || []);
          if (excludeValues.length > 0) {
            excludeValues.forEach((v) =>
              query.append(`exclude${capKey}`, v),
            );
          }
          break;
        }
        case "number":
          if (value && value.operator) {
            switch (value.operator) {
              case "lessThan":
                if (value.to !== "") query.set(`${key}_lt`, value.to);
                break;
              case "greaterThan":
                if (value.from !== "") query.set(`${key}_gt`, value.from);
                break;
              case "between":
                if (value.from !== "") query.set(`${key}_gt`, value.from);
                if (value.to !== "") query.set(`${key}_lt`, value.to);
                break;
            }
          }
          break;
        case "date":
          if (value && value.operator) {
            switch (value.operator) {
              case "is":
                if (value.from)
                  query.set(`${key}_gte`, toISO(value.from));
                if (value.to) query.set(`${key}_lte`, addOneDay(value.to));
                break;
              case "before":
                if (value.to)
                  query.set(`${key}_lte`, toISO(value.to));
                break;
              case "after":
                if (value.from)
                  query.set(`${key}_gte`, toISO(value.from));
                break;
              case "between":
                if (value.from)
                  query.set(`${key}_gte`, toISO(value.from));
                if (value.to) query.set(`${key}_lte`, addOneDay(value.to));
                break;
            }
          }
          break;
        default:
          break;
      }
    });
  }

  Object.keys(extraParams).forEach((key) => {
    if (extraParams[key]) {
      query.set(key, extraParams[key]);
    }
  });

  return query.toString();
};
